import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
const SecuritySettings = () => {
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [qrCode, setQrCode] = useState('');
    const [verifyToken, setVerifyToken] = useState('');
    const [backupCodes, setBackupCodes] = useState([]);
    const [showBackupCodes, setShowBackupCodes] = useState(false);
    const [disablePassword, setDisablePassword] = useState('');
    const [showDisableDialog, setShowDisableDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [setupMode, setSetupMode] = useState(false);
    const { toast } = useToast();
    useEffect(() => {
        checkMFAStatus();
    }, []);
    const checkMFAStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/mfa/status`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            const data = await response.json();
            setMfaEnabled(data.mfaEnabled);
        }
        catch (error) {
            console.error('Failed to check MFA status:', error);
        }
    };
    const handleSetupMFA = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/mfa/setup`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) {
                throw new Error('Failed to setup MFA');
            }
            const data = await response.json();
            setQrCode(data.qrCode);
            setSetupMode(true);
            toast({
                title: 'QR Code Generated',
                description: 'Scan this code with Google Authenticator',
            });
        }
        catch (error) {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        }
        finally {
            setLoading(false);
        }
    };
    const handleVerifyMFA = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/mfa/verify`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token: verifyToken }),
            });
            if (!response.ok) {
                throw new Error('Invalid verification code');
            }
            const data = await response.json();
            setBackupCodes(data.backupCodes);
            setShowBackupCodes(true);
            setMfaEnabled(true);
            setSetupMode(false);
            setQrCode('');
            setVerifyToken('');
            toast({
                title: 'MFA Enabled!',
                description: '⚠️ Save your backup codes now!',
            });
        }
        catch (error) {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        }
        finally {
            setLoading(false);
        }
    };
    const handleDisableMFA = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/mfa/disable`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ password: disablePassword }),
            });
            if (!response.ok) {
                throw new Error('Invalid password');
            }
            setMfaEnabled(false);
            setShowDisableDialog(false);
            setDisablePassword('');
            toast({
                title: 'MFA Disabled',
                description: 'Two-factor authentication has been turned off',
            });
        }
        catch (error) {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        }
        finally {
            setLoading(false);
        }
    };
    const copyBackupCodes = () => {
        navigator.clipboard.writeText(backupCodes.join('\n'));
        toast({
            title: 'Copied!',
            description: 'Backup codes copied to clipboard',
        });
    };
    const downloadBackupCodes = () => {
        const blob = new Blob([backupCodes.join('\n')], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'qbcron-mfa-backup-codes.txt';
        a.click();
        URL.revokeObjectURL(url);
        toast({
            title: 'Downloaded!',
            description: 'Backup codes saved to file',
        });
    };
    return (_jsxs("div", { className: "p-6 space-y-6", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-3xl font-bold", children: "Security Settings" }), _jsx("p", { className: "text-muted-foreground mt-2", children: "Manage two-factor authentication for your account" })] }), _jsxs(Card, { children: [_jsxs(CardHeader, { children: [_jsxs(CardTitle, { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-3xl", children: "\uD83D\uDD10" }), "Two-Factor Authentication"] }), _jsx(CardDescription, { children: "Add an extra layer of security to your account with Google Authenticator" })] }), _jsxs(CardContent, { className: "space-y-4", children: [_jsxs("div", { className: "flex items-center justify-between p-4 bg-muted rounded-lg", children: [_jsxs("div", { children: [_jsx("p", { className: "font-medium", children: "Status" }), _jsx("p", { className: "text-sm text-muted-foreground", children: mfaEnabled ? 'Two-factor authentication is enabled' : 'Two-factor authentication is disabled' })] }), _jsx("div", { className: `px-4 py-2 rounded-full font-semibold ${mfaEnabled ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'}`, children: mfaEnabled ? '✓ ENABLED' : '✗ DISABLED' })] }), !mfaEnabled && !setupMode && (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "p-4 border border-blue-500 bg-blue-50 dark:bg-blue-950 rounded-lg", children: [_jsx("p", { className: "font-medium text-blue-900 dark:text-blue-100", children: "Why enable MFA?" }), _jsxs("ul", { className: "mt-2 space-y-1 text-sm text-blue-800 dark:text-blue-200", children: [_jsx("li", { children: "\u2022 Protects against password theft" }), _jsx("li", { children: "\u2022 Prevents unauthorized access even if password is leaked" }), _jsx("li", { children: "\u2022 Industry-standard security (used by banks, governments)" }), _jsx("li", { children: "\u2022 Works with any TOTP app (Google Authenticator, Authy, etc.)" })] })] }), _jsx(Button, { onClick: handleSetupMFA, disabled: loading, className: "w-full", children: loading ? 'Setting up...' : 'Enable Two-Factor Authentication' })] })), setupMode && qrCode && (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "p-6 bg-white dark:bg-gray-900 rounded-lg border-2 border-dashed", children: [_jsx("h3", { className: "font-semibold mb-4 text-center", children: "Step 1: Scan QR Code" }), _jsx("img", { src: qrCode, alt: "MFA QR Code", className: "mx-auto w-64 h-64" }), _jsx("p", { className: "text-sm text-muted-foreground text-center mt-4", children: "Open Google Authenticator on your phone and scan this code" })] }), _jsxs("form", { onSubmit: handleVerifyMFA, className: "space-y-4", children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "verifyToken", children: "Step 2: Enter Verification Code" }), _jsx(Input, { id: "verifyToken", type: "text", placeholder: "000000", value: verifyToken, onChange: (e) => setVerifyToken(e.target.value.replace(/\D/g, '').slice(0, 6)), maxLength: 6, className: "text-center text-2xl tracking-widest font-mono", required: true, autoFocus: true }), _jsx("p", { className: "text-sm text-muted-foreground mt-2", children: "Enter the 6-digit code shown in Google Authenticator" })] }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { type: "submit", disabled: loading || verifyToken.length !== 6, className: "flex-1", children: loading ? 'Verifying...' : 'Verify & Enable' }), _jsx(Button, { type: "button", variant: "outline", onClick: () => {
                                                            setSetupMode(false);
                                                            setQrCode('');
                                                            setVerifyToken('');
                                                        }, children: "Cancel" })] })] })] })), mfaEnabled && (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "p-4 bg-green-50 dark:bg-green-950 border border-green-500 rounded-lg", children: [_jsx("p", { className: "font-medium text-green-900 dark:text-green-100", children: "\u2713 Your account is protected" }), _jsx("p", { className: "text-sm text-green-800 dark:text-green-200 mt-1", children: "You'll need to enter a code from Google Authenticator when logging in" })] }), _jsx(Button, { variant: "destructive", onClick: () => setShowDisableDialog(true), className: "w-full", children: "Disable Two-Factor Authentication" })] }))] })] }), _jsx(AlertDialog, { open: showBackupCodes, onOpenChange: setShowBackupCodes, children: _jsxs(AlertDialogContent, { className: "max-w-2xl", children: [_jsxs(AlertDialogHeader, { children: [_jsx(AlertDialogTitle, { className: "text-2xl", children: "\u26A0\uFE0F Save Your Backup Codes" }), _jsx(AlertDialogDescription, { children: "Store these codes in a safe place. Each code can be used once to login if you lose access to your authenticator app." })] }), _jsxs("div", { className: "space-y-4", children: [_jsx("div", { className: "grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm", children: backupCodes.map((code, index) => (_jsx("div", { className: "p-2 bg-background rounded border", children: code }, index))) }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { onClick: copyBackupCodes, variant: "outline", className: "flex-1", children: "\uD83D\uDCCB Copy Codes" }), _jsx(Button, { onClick: downloadBackupCodes, variant: "outline", className: "flex-1", children: "\uD83D\uDCBE Download Codes" })] }), _jsx("div", { className: "p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-500 rounded-lg", children: _jsxs("p", { className: "text-sm text-yellow-800 dark:text-yellow-200", children: [_jsx("strong", { children: "Important:" }), " You won't be able to see these codes again. Save them now!"] }) })] }), _jsx(AlertDialogFooter, { children: _jsx(AlertDialogAction, { onClick: () => setShowBackupCodes(false), children: "I've Saved My Backup Codes" }) })] }) }), _jsx(AlertDialog, { open: showDisableDialog, onOpenChange: setShowDisableDialog, children: _jsxs(AlertDialogContent, { children: [_jsxs(AlertDialogHeader, { children: [_jsx(AlertDialogTitle, { children: "Disable Two-Factor Authentication?" }), _jsx(AlertDialogDescription, { children: "This will make your account less secure. You'll only need your password to login." })] }), _jsx("div", { className: "space-y-4", children: _jsxs("div", { children: [_jsx(Label, { htmlFor: "disablePassword", children: "Enter your password to confirm" }), _jsx(Input, { id: "disablePassword", type: "password", value: disablePassword, onChange: (e) => setDisablePassword(e.target.value), placeholder: "Your password" })] }) }), _jsxs(AlertDialogFooter, { children: [_jsx(AlertDialogCancel, { onClick: () => setDisablePassword(''), children: "Cancel" }), _jsx(AlertDialogAction, { onClick: handleDisableMFA, disabled: loading || !disablePassword, className: "bg-destructive text-destructive-foreground hover:bg-destructive/90", children: loading ? 'Disabling...' : 'Disable MFA' })] })] }) })] }));
};
export default SecuritySettings;
